import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Toggle,
  Button,
  Banner,
  Flex,
  Icon,
  Tooltip,
  TooltipContent,
  Paragraph,
  Link,
  FormattedDateTime,
  FormLayout,
  TextLabel,
  Box,
} from '@customerio/pluma-components/react';
import { Form, TextField, TextArea, useForm } from '@/components/form';
import { useStore } from '@tanstack/react-form';
import { servicesApi } from '@/services/api';
import { useSnackbars } from '@customerio/pluma-components/react';
import { generate } from '@customerio/hydra-copilot';
import { z } from 'zod';
import zodToJsonSchema from 'zod-to-json-schema';
import { sentryCaptureException } from '@/utils/sentry';
import { supportFormOptions, GOAL_PLACEHOLDER, isDefaultGoalText } from '../helpers/form-schema';
import { MIN_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS, MAX_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS } from '../types';
import type { SupportFormModalData } from '../types';
import * as styles from './support-form-modal.css';
import { useEffect, useRef } from 'react';
import { useAnalytics } from '@/services/analytics';
import { Events } from '@/services/analytics/types';

export function SupportFormModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<SupportFormModalData>) {
  const snackbars = useSnackbars();
  const { track } = useAnalytics();

  const hasTrackedStarted = useRef(false);
  useEffect(() => {
    if (data.source === 'cancellation_flow' && !hasTrackedStarted.current && data.workspaceId != null) {
      hasTrackedStarted.current = true;
      void servicesApi.trackEvent({
        params: { workspaceId: data.workspaceId },
        body: {
          event: Events.plan_and_billing.supportRequestStarted.name,
          properties: {
            accountId: data.accountId,
            workspaceId: data.workspaceId,
            source: data.source,
          },
        },
      });
    }
  }, [data.source, data.accountId, data.workspaceId]);

  const form = useForm({
    ...supportFormOptions,
    defaultValues: {
      ...supportFormOptions.defaultValues,
      goal: data.initialProblem ?? supportFormOptions.defaultValues.goal,
      consentToAccess: false,
    },
    async onSubmit({ value }) {
      void track('inline_support', 'submitClicked', data.trackingProperties);

      try {
        if (!data.supportTeamAccessEnabled && value.consentToAccess) {
          const now = Math.floor(Date.now() / 1000);
          const timestamp = value.consentToAccess ? now + value.daysLimit * 24 * 60 * 60 : 0;

          try {
            await servicesApi.setSupportAccess({
              params: { accountId: data.accountId },
              body: {
                Account: {
                  access_granted_until: timestamp,
                },
              },
            });
            void track('inline_support', 'supportAccessGranted', data.trackingProperties);
          } catch (error) {
            void track('inline_support', 'supportAccessError', data.trackingProperties);
            throw error;
          }
        }

        let subject = '';
        try {
          const result = await generate(
            'Generate a short, concise subject line for this support ticket based on the problem description. The subject should help support triage the issue quickly. Maximum 85 characters.',
            zodToJsonSchema(
              z.object({
                subject: z.string().describe('A short subject line for the support ticket').min(1).max(85),
              }),
            ),
            {
              context: [
                {
                  description: 'The user problem description',
                  value: value.goal,
                },
              ],
              includeGlobalContext: false,
              includeGlobalTools: false,
              includeGlobalInstructions: false,
            },
          );
          subject = result.subject;
        } catch (error) {
          sentryCaptureException(error, {
            level: 'warning',
            tags: { feature: 'support-form-ai-subject-generation' },
          });
          subject = '';
        }

        await servicesApi.submitSupportRequest({
          params: { accountId: data.accountId },
          body: {
            comment: subject,
            goal: value.goal,
            video: value.video,
            replyTo: data.userEmail,
            consent_to_access: value.consentToAccess,
            browser: navigator.userAgent,
            url: window.location.href,
            windowDimensions: `${window.innerWidth}x${window.innerHeight}`,
          },
        });

        snackbars.create({
          message: "Your support request has been submitted. We'll get back to you soon!",
          variant: 'success',
        });

        void track('inline_support', 'submitSuccess', data.trackingProperties);

        if (data.source === 'cancellation_flow' && data.workspaceId != null) {
          void servicesApi.trackEvent({
            params: { workspaceId: data.workspaceId },
            body: {
              event: Events.plan_and_billing.supportRequestCompleted.name,
              properties: {
                accountId: data.accountId,
                workspaceId: data.workspaceId,
                comment: value.goal,
                source: data.source,
              },
            },
          });
        }

        onConfirm(value);
      } catch (error) {
        void track('inline_support', 'submitError', data.trackingProperties);
        sentryCaptureException(error);
        snackbars.create({
          message: 'Unable to submit support request. Please try again.',
          variant: 'error',
        });
      }
    },
  });

  const consentToAccess = useStore(form.store, (state) => state.values.consentToAccess);
  const canSubmit = useStore(form.store, (state) => state.canSubmit && !isDefaultGoalText(state.values.goal));

  const displayAccessConsentExplanation = !data.hasEditPermission && !data.supportTeamAccessEnabled;

  const handleClose = () => {
    void track('inline_support', 'closed', data.trackingProperties);
    onClose();
  };

  return (
    <Modal state={modalState} size="md" title="Describe your support request">
      <ModalBody>
        <Form api={form} id="support-form">
          <FormLayout>
            <TextArea
              api={form}
              name="goal"
              label="Describe your problem"
              description="The more detail you can share the better we can help."
              placeholder={GOAL_PLACEHOLDER}
              minRows={5}
            />

            <TextField
              api={form}
              name="video"
              label="Share a video"
              placeholder="Video URL"
              description={
                <>
                  Videos are recommended but not required. eg.{' '}
                  <Link href="http://loom.com/" variant="secondary">
                    Loom
                  </Link>
                </>
              }
            />

            {data.hasEditPermission ? (
              <div>
                {data.supportTeamAccessEnabled ? (
                  <Banner variant="information">
                    Support access is currently granted until{' '}
                    <FormattedDateTime date={(data.accessGrantedUntil ?? 0) * 1000} format="full" />. You can review or
                    revoke access at any time on the{' '}
                    <Link href="/settings/privacy" isExternal>
                      Privacy & Data
                    </Link>{' '}
                    page.
                  </Banner>
                ) : (
                  <>
                    <Flex justifyContent="space-between" alignItems="center">
                      <TextLabel
                        id="consent-label"
                        htmlFor="consent-toggle"
                        display="inline-flex"
                        gap="050"
                        alignItems="center"
                      >
                        Grant support access
                        <Tooltip>
                          <Icon name="help" size="sm" />
                          <TooltipContent>
                            <Paragraph color="inherit" mb="200">
                              To help with issues, our customer support team may need access to your account. If you
                              choose to provide access, only authorized team members will have access while they help
                              with your requests.
                            </Paragraph>
                            <Paragraph color="inherit">
                              You can review or revoke access at any time on the Privacy & Data page found in Account
                              Settings.
                            </Paragraph>
                          </TooltipContent>
                        </Tooltip>
                      </TextLabel>
                      <Toggle
                        id="consent-toggle"
                        ariaLabelledby="consent-label"
                        size="sm"
                        labelOn="Yes"
                        labelOff="No"
                        isChecked={consentToAccess}
                        onChange={(e) => form.setFieldValue('consentToAccess', e.currentTarget.checked)}
                      />
                    </Flex>

                    {consentToAccess ? (
                      <Box>
                        <Flex alignItems="center" gap="150">
                          <span>Allow access for</span>
                          <TextField
                            api={form}
                            ariaLabel="Number of days to allow support team access"
                            name="daysLimit"
                            type="number"
                            min={MIN_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS}
                            max={MAX_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS}
                            isDisabled={!data.hasEditPermission}
                            className={styles.daysLimitInput}
                          />
                          <span>days.</span>
                        </Flex>
                      </Box>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}

            {displayAccessConsentExplanation ? (
              <Banner title="We might need access to your account to help you">
                Contact your account administrator to grant us access under{' '}
                <Flex alignItems="center" display="inline-flex">
                  Settings
                  <Icon name="chevron-right" size="xxs" />
                  Account Settings
                  <Icon name="chevron-right" size="xxs" />
                  <Link href="/settings/privacy" isExternal>
                    Privacy, Data & AI
                  </Link>
                  .
                </Flex>
              </Banner>
            ) : null}
          </FormLayout>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="subtle" onClick={handleClose} isDisabled={form.state.isSubmitting}>
          Cancel
        </Button>
        <Button variant="primary" form="support-form" type="submit" isDisabled={!canSubmit}>
          Submit ticket
        </Button>
      </ModalFooter>
    </Modal>
  );
}
